import React from 'react';
import { useIntl } from 'react-intl';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { Loader, SdirModal } from '@sdir/sds';
import { FormInput } from '@sdir/blueprint.aps/lib/components';
import { ErrorMessage } from '@sdir/utilities/lib/components';

interface AddReporteeModalProps {
  visible: boolean;
  loading?: boolean;
  error?: any;
  onCancel: () => void;
  onConfirm: (values: AddReporteeInput) => void;
}

export interface AddReporteeInput {
  email: string;
  note: string;
}

const AddReporteeModal: React.FC<AddReporteeModalProps> = ({
  error,
  loading,
  visible,
  onCancel,
  onConfirm,
}) => {
  const intl = useIntl();
  const requiredText = intl.formatMessage({ id: 'general.form.error' });
  const initialValues: AddReporteeInput = {
    email: '',
    note: '',
  };
  const validationSchema: Yup.SchemaOf<AddReporteeInput> = Yup.object().shape({
    email: Yup.string().required(requiredText),
    note: Yup.string().required(requiredText),
  });

  const onSubmit = (values: AddReporteeInput) => {
    onConfirm(values);
  };

  if (!visible) return <></>;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting }) => {
        return (
          <StyledModal
            visible={visible}
            width="600px"
            okText={intl.formatMessage({ id: 'general.add' })}
            cancelText={intl.formatMessage({ id: 'general.cancel' })}
            onCancel={onCancel}
            onOK={handleSubmit}
          >
            <form data-testid="rm-confirm-form" onSubmit={handleSubmit}>
              <h2>Add firm</h2>
              <Fields>
                <Field
                  name="email"
                  label={intl.formatMessage({ id: 'general.form.email' })}
                  type="text"
                  component={FormInput}
                  disabled={isSubmitting}
                />
                <Field
                  name="note"
                  label={intl.formatMessage({ id: 'usermanagement.table.note' })}
                  type="text"
                  component={FormInput}
                  disabled={isSubmitting}
                />
              </Fields>
            </form>
            {loading && <Loader />}
            {error && (
              <ErrorWrapper>
                <ErrorMessage error={error} />
              </ErrorWrapper>
            )}
          </StyledModal>
        );
      }}
    </Formik>
  );
};

export default AddReporteeModal;

const StyledModal = styled(SdirModal)`
  font-size: 2rem;
`;

const Fields = styled.div`
  & > div {
    margin-bottom: 15px;
  }
`;

const ErrorWrapper = styled.div`
  margin-bottom: 20px;
`;
